import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { EnvironmentProviders, Provider, provideAppInitializer, inject } from "@angular/core";
import { MsalFactoryService } from "../services/msal-factory.service";
import { HttpInterceptor } from "../interceptors/http.interceptor";
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalService,
} from "@azure/msal-angular";
import { AuthInterceptor } from "../interceptors/auth.interceptor";
import { ModalService } from "../services/modal.service";
import { AuthService } from "../services/auth.service";

/**
 * Initializes MSAL service with account storage events
 * This runs before the application starts
 */
function initializeMsal(msalService: MsalService): void {
  // Enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
  msalService.instance.enableAccountStorageEvents();
  console.debug("MSAL initialized with account storage events enabled");
}

/**
 * Provides authentication-related services and configuration for the application.
 * This includes MSAL (Microsoft Authentication Library) setup, HTTP interceptors,
 * and authentication services.
 *
 * @returns Array of providers for authentication functionality
 */
export function provideAuth(): (Provider | EnvironmentProviders)[] {
  const msalFactoryService = new MsalFactoryService();

  return [
    // Core authentication services
    MsalFactoryService,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    ModalService,
    AuthService,

    // MSAL configuration providers
    {
      provide: MSAL_INSTANCE,
      useFactory: msalFactoryService.MsalInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: msalFactoryService.MsalGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: msalFactoryService.MsalInterceptorConfigFactory,
    },

    // HTTP interceptors
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },

    // Initialize MSAL before app starts (using modern provideAppInitializer)
    provideAppInitializer(() => {
      const msalService = inject(MsalService);
      initializeMsal(msalService);
    }),
  ];
}
